<script setup>
import { driver } from 'driver.js';
import i18next from 'i18next';
import Swiper, { Navigation } from 'swiper';
import { computed, nextTick, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { fetchData } from '../../../fetchData.js';
import { formatDateByRegion } from '../../../helpers.js';
import Disclaimer from '../../customer/Disclaimer.vue';
import Popup from '../../miscellaneous/Popup.vue';
import Sheet from '../../Sheet.vue';
import SkeletonLoader from '../../SkeletonLoader.vue';
import TutorialBanner from '../../TutorialBanner.vue';
import ContentstreamsListV2 from '../ContentstreamsListV2.vue';
import MarketsHeader from '../MarketsHeader.vue';
import Path from '../Path.vue';
import FullScreenImageSlider from './FullScreenImageSlider.vue';
import ImageSlider from './ImageSlider.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();

const userId = ref(null);
const productId = ref('');
const analysis = ref([]);
const analysisName = ref('');
const productImage = ref([]);
const imagesIndicatorAnalysis = ref([]);
const productGroupShortDescription = ref('');
const contentstreams = ref([]);
const favoriteStatus = ref(false);
const content = ref([]);
const images = ref([]);
const slider = ref([]);
const sliderIndicator = ref([]);
const productGroup = ref('');
const fullscreen = ref(false);
const loading = ref(true);
const activeTab = ref(0);
const contentstreamType = ref(null);
const fullScreenScope = ref('slider');
const currentProductGroup = ref(null);
const indicatorSlider = ref(null);
const analysisText = ref(null);
const fastAccess = ref(null);
const showRelatedLinksPopup = ref(false);

const startHelpTour = () => {
    const steps = [
        {
            popover: {
                title: i18next.t('indicators.analysis_intro.title'),
                description: i18next.t('indicators.analysis_intro.description'),
                side: 'bottom',
                align: 'start'
            }
        },
        {
            element: indicatorSlider.value?.$el || undefined,
            popover: {
                title: i18next.t('indicators.charts.title'),
                description: i18next.t('indicators.charts.description'),
                side: 'top',
                align: 'start'
            }
        },
        {
            element: analysisText.value?.$el || undefined,
            popover: {
                title: i18next.t('indicators.description.title'),
                description: i18next.t('indicators.description.description'),
                side: 'top',
                align: 'start'
            }
        },
        {
            element: fastAccess.value?.localHeader || undefined,
            popover: {
                title: i18next.t('indicators.latest.title'),
                description: i18next.t('indicators.latest.description'),
                side: 'top',
                align: 'start'
            },
            onHighlightStarted: () => {
                fastAccess.value?.localHeader?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
            }
        },
        {
            popover: {
                title: i18next.t('indicators.more_info.title'),
                description: i18next.t('indicators.more_info.description'),
                side: 'top',
                align: 'start'
            }
        },
        {
            popover: {
                title: i18next.t('indicators.support.title'),
                description: i18next.t('indicators.signoff.description'),
                side: 'top',
                align: 'start'
            }
        }
    ];

    const driverObj = driver({
        allowHtml: true,
        showProgress: true,
        nextBtnText: i18next.t('plain_next'),
        prevBtnText: i18next.t('plain_back'),
        doneBtnText: i18next.t('plain_done'),
        steps
    });

    driverObj.drive();
};
Swiper.use([Navigation]);

const getMarketProductGroups = async () => {
    try {
        const { result = [] } = await fetchData(`/api/v2/product-groups/${productGroup.value.id}`);
        currentProductGroup.value = { products: { data: result.data.products } };
    } catch (error) {
        console.error('Error fetching market product groups:', error);

        currentProductGroup.value = null;
    }
};

const waitForContentLoading = (content, continueFunction = null, continueFunctions = []) => {
    const interval = setInterval(() => {
        if (store.getters['settings/isContentLoaded'](content)) {
            if (continueFunctions.length > 0) {
                waitForContentLoading(continueFunctions[0], continueFunction, continueFunctions.shift);
            } else if (continueFunction) {
                continueFunction();
            }

            clearInterval(interval);
        }
    }, 10);
};

const getPastAnalysis = (productID) => {
    contentstreams.value = store.getters['contentstreams/all'].dashboard.data
        .filter((item) => productID.includes(item.product_id))
        .sort((a, b) => new Date(b.published_at) - new Date(a.published_at));
};

const toggleFavoriteReadStatus = async (contentstream, currentContentStream = false) => {
    const update = () => {
        const contentstreamFavorite = contentstream.read_status === null ? true : !contentstream.read_status.is_favorite;

        store.dispatch('contentstream_user/update', {
            userId,
            contentstreamId: contentstream.id,
            isFavorite: contentstreamFavorite
        });

        if (currentContentStream) {
            favoriteStatus.value = !favoriteStatus.value;
        } else {
            contentstream.read_status = { is_favorite: contentstreamFavorite };
        }
    };

    if (contentstream.read_status === null) {
        initReadStatus(contentstream.id);
    }

    setTimeout(update, 100);
};

const changeActiveTab = (_, key) => {
    if (key !== undefined) {
        activeTab.value = Number(key);

        if (sliderIndicator.value?.slideTo) {
            sliderIndicator.value.slideTo(key);
        }
    }
};
const hasImageValue = (item) => {
    const itemTitle = item?.attributes?.title?.de?.toLowerCase() || '';

    return images.value.findIndex((image) => {
        const imageTitle = image?.attributes?.title?.de?.toLowerCase() || '';

        return imageTitle.includes(itemTitle);
    });
};
const changeActiveTabFromIndicator = (item, key) => {
    if (key !== undefined) {
        activeTab.value = Number(key);
        const index = hasImageValue(item);

        if (index !== -1) {
            slideToImage(index);
        } else {
            if (sliderIndicator.value?.slideTo) {
                sliderIndicator.value.slideTo(key);
            }

            slideToImage(key);
        }
    }
};
const initReadStatus = (contentstreamID) => {
    store.dispatch('contentstream_user/init', {
        contentstreamId: contentstreamID,
        isRead: true
    });
};

const collectImages = () => {
    images.value = content.value.flatMap((item) => {
        if (item?.layout === 'image') return [item];

        if (item?.layout === 'tab_wysiwyg_with_images' && item?.attributes?.images?.length > 0) {
            return item.attributes.images;
        }

        return [];
    });
};

const toggleFullscreen = (image, scope) => {
    fullScreenScope.value = scope;
    fullscreen.value = !fullscreen.value;
};

const slideToImage = (index) => {
    if (slider.value?.slideTo) {
        slider.value.slideTo(index);
    }
};

const initSlider = () => {
    slider.value = new Swiper('.image-slider', {
        slidesPerView: 1,
        imageSize: 'cover'
    });

    if (imagesIndicatorAnalysis.value.length) {
        sliderIndicator.value = new Swiper('.image-indicator', {
            slidesPerView: 1,
            imageSize: 'cover'
        });
    }
};

const createPreviousURL = () => {
    if (!productGroup.value?.id) {
        return '';
    }

    const urlMap = {
        doubleProduct: `/dashboard/markets-double/${productGroup.value.id}`,
        bundleProduct: `/dashboard/markets-package/${productGroup.value.id}`
    };

    return urlMap[productGroup.value?.productType] || `/dashboard/markets/${productGroup.value?.id || ''}`;
};

const goBack = () => {
    const lastRoute = sessionStorage.getItem('lastRoute');

    if (lastRoute) {
        router.push(lastRoute);
    } else {
        router.push('/dashboard');
    }
};

const tabContent = computed(() => analysis.value?.content?.filter((item) => ['tab_wysiwyg', 'tab_wysiwyg_with_images'].includes(item.layout)) || []);

const updateActiveImageTab = (event) => {
    if (Array.isArray(event.attributes.images)) {
        const firstImageIndex = images.value.findIndex((image) => image.key === event.attributes.images[0]?.key);

        if (firstImageIndex !== -1) {
            slideToImage(firstImageIndex);
        }
    }
};

const getContentstreamData = async (contentstreamId) => {
    const isSpecialReportTitles = [{ de: 'Sondernachricht', en: 'Special Message' }];

    await store.dispatch('contentstream/show', contentstreamId);
    analysis.value = store.getters['contentstream/get'];

    imagesIndicatorAnalysis.value = analysis.value.content?.flatMap((item) => item?.attributes?.images_indicator_analysis || []).filter(Boolean);

    productImage.value = analysis.value.product?.image || null;
    analysisName.value = analysis.value.product?.name || analysis.value.title;
    productId.value = analysis.value.product?.id || 'unknown-product-id';

    const title = analysis.value.contentstreamType?.title;
    const isSpecialReport = isSpecialReportTitles.some((entry) => entry.de === title?.de || entry.en === title?.en);
    productGroupShortDescription.value = analysis.value.product?.productGroups?.data?.[0]?.short_description || (isSpecialReport ? '' : 'No description available');

    content.value = analysis.value.content || [];
    productGroup.value = analysis.value?.product?.productGroups?.data?.[0] || null;
    contentstreamType.value = ['short', 'video'].includes(analysis.value.contentstreamType.name) ? analysis.value.contentstreamType.name : '';

    collectImages();
    waitForContentLoading('contentstreams', () => getPastAnalysis(productId.value));
    nextTick(initSlider);
    loading.value = false;
};

onMounted(async () => {
    await store.dispatch('user/user');
    userId.value = store.getters['user/get'].id;
    await getContentstreamData(route.params.id);
    await getMarketProductGroups();
});

router.afterEach((to, from) => {
    if (to.params.id !== from.params.id && to.name === from.name) {
        loading.value = true;

        getContentstreamData(to.params.id);
    }

    activeTab.value = 0;
});
</script>

<template>
    <div>
        <div class="min-h-screen w-full bg-[#f1f6fa] px-6 lg:px-20 lg:py-10 flex flex-col gap-6">
            <MarketsHeader v-if="currentProductGroup" ref="fastAccess" :current-product-group="currentProductGroup" :to-overview="createPreviousURL()" />
            <div class="flex flex-col xl:flex-row justify-between m-2 xl:mt-0 gap-4">
                <button
                    class="flex items-center gap-2 xl:gap-4 cursor-pointer rounded-lg bg-white w-full xl:w-fit px-3 py-2 text-sm hover:shadow-md hover:scale-105 duration-300 transition font-bold"
                    @click="goBack"
                >
                    <i class="fi fi-rr-arrow-left text-lg mt-2" />
                    {{ $t('plain_back') }}
                </button>

                <Path :sticky="false" class="flex flex-wrap items-center gap-2 text-sm xl:text-base">
                    <router-link to="/dashboard" class="text-blue-500 whitespace-nowrap">Dashboard</router-link>
                    <i class="flex fi fi-rr-angle-small-right"></i>
                    <router-link to="/dashboard/markets" class="text-blue-500 whitespace-nowrap">{{ $t('plain_analysis') }}</router-link>
                    <template v-if="analysis.product">
                        <i class="flex fi fi-rr-angle-small-right"></i>
                        <router-link v-if="createPreviousURL() !== ''" :to="createPreviousURL()" class="text-blue-500 cursor-pointer whitespace-nowrap">
                            {{ $translate(analysisName) }}
                        </router-link>
                    </template>
                    <template v-if="analysis.published_at">
                        <i class="flex fi fi-rr-angle-small-right"></i>
                        <p class="text-blue-500 whitespace-nowrap">{{ formatDateByRegion($moment(analysis.published_at)) }}</p>
                    </template>
                </Path>
            </div>
            <!--Path navigation ends-->
            <div class="space-y-4">
                <TutorialBanner v-if="imagesIndicatorAnalysis.length" :title="$t('indicators.new_feature')" :show-until="'2025-10-01'" @start-tutorial="startHelpTour" />
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <Sheet ref="analysisText">
                            <SkeletonLoader v-if="loading" height="40px" :rows="20" />
                            <div v-if="tabContent.length && !loading" ref="content">
                                <div class="flex justify-between items-center mb-2">
                                    <button
                                        v-if="analysis?.related_links && analysis.related_links.length > 0"
                                        class="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 transition-colors cursor-pointer"
                                        @click="showRelatedLinksPopup = true"
                                    >
                                        <i class="fi fi-rr-link text-sm"></i>
                                        <span class="text-sm font-medium">{{ $t('Related_Links') }}</span>
                                    </button>
                                    <div v-else></div>
                                    <info-text title="Info" :text="$t('AnalysisPost.Info')" />
                                </div>
                                <Tabs
                                    v-if="tabContent.length > 1"
                                    :data="tabContent"
                                    :active-tab="activeTab || 0"
                                    active-color="#353CEE"
                                    :first-on-load="true"
                                    @update:active-tab="
                                        (item, key) => {
                                            changeActiveTab(item, key), updateActiveImageTab(item);
                                        }
                                    "
                                >
                                    <template #header="{ data }">
                                        <strong class="whitespace-nowrap">
                                            {{ $translate(data?.attributes.title) ? $translate(data?.attributes?.title) : $t('plain_analysis') }}
                                        </strong>
                                    </template>

                                    <template #item="{ key, data }">
                                        <div v-if="data.attributes?.wysiwyg">
                                            <div class="content fr-view" v-html="$translate(data.attributes.wysiwyg)"></div>
                                        </div>
                                    </template>
                                </Tabs>

                                <!-- WITHOUT Tabs -->
                                <div v-else>
                                    <div v-if="tabContent[0]?.attributes?.wysiwyg">
                                        <div class="content fr-view" v-html="$translate(tabContent[0].attributes.wysiwyg)"></div>
                                    </div>
                                </div></div
                        ></Sheet>
                    </div>
                    <div class="relative">
                        <div class="sticky top-44 space-y-4">
                            <Sheet class="mb-4">
                                <SkeletonLoader v-if="loading" height="400px" />
                                <ImageSlider
                                    v-if="images.length && !loading"
                                    scope="slider"
                                    :images="images"
                                    :tab-content="tabContent"
                                    :active-slider-index="slider?.activeIndex || 0"
                                    @toggle-fullscreen="toggleFullscreen"
                                    @change-active-tab="(_, index) => changeActiveTab(undefined, index)"
                                    @slide-to-image="slideToImage"
                            /></Sheet>

                            <Sheet v-if="imagesIndicatorAnalysis.length" class="mb-4">
                                <ImageSlider
                                    v-if="imagesIndicatorAnalysis.length"
                                    ref="indicatorSlider"
                                    scope="indicator"
                                    :tab-content="tabContent"
                                    :images="imagesIndicatorAnalysis"
                                    :active-slider-index="activeTab || 0"
                                    @toggle-fullscreen="toggleFullscreen"
                                    @change-active-tab-from-indicator="(item, index) => changeActiveTabFromIndicator(item, index)"
                                    @slide-to-image="slideToImage"
                                />
                            </Sheet>
                            <Sheet v-if="loading">
                                <SkeletonLoader height="80px" :rows="10" />
                            </Sheet>
                            <div v-if="productGroup && !loading">
                                <ContentstreamsListV2
                                    class="shadow-md"
                                    :show-indicator="analysis.indicator_analysis"
                                    :page-size="analysis.indicator_analysis ? 20 : 40"
                                    :product-id="productId"
                                    :init-filter="contentstreamType ? [contentstreamType] : ['wmu', 'dsu', 'dmu']"
                                    :show-dropdown="false"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <FullScreenImageSlider
                    v-if="fullscreen"
                    :full-screen-scope="fullScreenScope"
                    :images="fullScreenScope === 'slider' ? images : imagesIndicatorAnalysis"
                    :active-modal-tab="activeTab"
                    :image-slider-init-value="fullScreenScope === 'slider' ? slider.activeIndex || 0 : activeTab || 0"
                    :tab-content="tabContent"
                    @toggle-fullscreen="toggleFullscreen"
                    @change-active-tab="(index) => changeActiveTab(undefined, index)"
                    @slide-to-image="slideToImage"
                />
            </div>
            <Disclaimer class="mt-6" />
        </div>

        <!-- Related Links Popup -->
        <Popup :open="showRelatedLinksPopup" :padding="true" @open="showRelatedLinksPopup = $event">
            <div class="max-w-2xl">
                <h2 class="text-2xl font-bold mb-4">{{ $t('Related_Links') }}</h2>
                <ul class="space-y-3">
                    <li v-for="(link, index) in analysis?.related_links" :key="index" class="border-b border-gray-200 pb-2 last:border-0">
                        <a
                            :href="link.fields.url"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-2 transition-colors"
                        >
                            <i class="fi fi-rr-link text-sm shrink-0"></i>
                            <span>{{ link.fields.title || link.fields.url }}</span>
                        </a>
                    </li>
                </ul>
            </div>
        </Popup>
    </div>
</template>
